import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement

class VerifyEmailPage(
    private val onLoginClick: () -> Unit,
    // Pre-extracted values from the URL (passed by the caller to avoid re-reading stale URL)
    private val emailVerified: Boolean? = null,
    private val verifyToken: String? = null,
) {
    private val apiService = ApiService()

    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.style.cssText = """
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            min-height: 100dvh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: linear-gradient(180deg, #0a0a0a 0%, #141418 100%); padding: 20px;
        """

        // Server-side flow: result passed directly (no URL reading needed)
        if (emailVerified != null) {
            container.innerHTML = if (emailVerified) buildCard(
                icon = "✅",
                title = "Email Verified!",
                message = "Your email has been verified successfully. You can now log in.",
                showLogin = true,
            ) else buildCard(
                icon = "❌",
                title = "Verification Failed",
                message = "This link is invalid or has already been used. Please request a new verification email.",
                showLogin = true,
            )
            attachListeners(container)
            return container
        }

        // Legacy flow: use pre-extracted token (or fall back to URL for backward compat)
        val hash = window.location.hash
        val search = window.location.search
        val token = verifyToken ?: when {
            search.contains("token=") -> search.substringAfter("token=").substringBefore("&").takeIf { it.isNotBlank() }
            hash.contains("token=") -> hash.substringAfter("token=").substringBefore("&").takeIf { it.isNotBlank() }
            else -> null
        }

        if (token == null) {
            container.innerHTML = buildCard(
                icon = "⚠️",
                title = "Invalid Link",
                message = "This verification link is invalid or malformed.",
                showLogin = true,
            )
            attachListeners(container)
            return container
        }

        // Show loading state
        container.innerHTML = buildCard(
            icon = "⏳",
            title = "Verifying...",
            message = "Please wait while we verify your email address.",
            showLogin = false,
        )

        // Call the verification endpoint
        GlobalScope.launch {
            try {
                val result = apiService.verifyEmail(token)
                if (result.isSuccess) {
                    container.innerHTML = buildCard(
                        icon = "✅",
                        title = "Email Verified!",
                        message = "Your email has been verified successfully. You can now log in.",
                        showLogin = true,
                    )
                } else {
                    container.innerHTML = buildCard(
                        icon = "❌",
                        title = "Verification Failed",
                        message = "This link is invalid or has expired. Please request a new verification email.",
                        showLogin = true,
                    )
                }
            } catch (e: Exception) {
                container.innerHTML = buildCard(
                    icon = "❌",
                    title = "Verification Failed",
                    message = "Something went wrong. Please try again later.",
                    showLogin = true,
                )
            }
            attachListeners(container)
        }

        return container
    }

    private fun buildCard(icon: String, title: String, message: String, showLogin: Boolean): String {
        val loginButton = if (showLogin) """
            <button id="verify-login-btn" style="
                width: 100%; padding: 14px; background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                color: #0a0a0a; border: none; border-radius: 8px; font-size: 16px; font-weight: 600;
                cursor: pointer; box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3); margin-top: 24px;
            ">Go to Login</button>
        """ else ""

        return """
            <div style="
                background: rgba(20, 20, 24, 0.9); border-radius: 16px; padding: 40px 32px;
                box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                width: 340px; max-width: 90vw; text-align: center;
            ">
                <div style="font-size: 48px; margin-bottom: 16px;">$icon</div>
                <h2 style="color: #fff; margin: 0 0 12px; font-size: 24px;">$title</h2>
                <p style="color: #999; font-size: 15px; line-height: 1.5; margin: 0;">$message</p>
                $loginButton
            </div>
        """
    }

    private fun attachListeners(container: HTMLElement) {
        container.querySelector("#verify-login-btn")?.addEventListener("click", {
            onLoginClick()
        })
    }
}
